// eslint-disable-next-line import/no-extraneous-dependencies
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google'
import getPreferences from 'api/getPreferences'
import googleIdToken from 'api/googleIdToken'
import { FC, useEffect, useState } from 'react'
import useIsTelegram from 'utils/hooks/useIsTelegram'

import GoogleButton from 'components/GoogleButton/GoogleButton'
import InAppBrowserModal from 'components/InAppBrowserModal/InAppBrowserModal'

interface ICallback {
  (response: any): void
}

interface IGoogleAuth {
  className?: string
  callback: ICallback
  buttonText?: string
  [key: string]: any
}

const GoogleButtonContainer: FC<IGoogleAuth> = ({ callback, ...props }) => {
  const [disabled, setDisabled] = useState(false)
  const [authCode, setAuthCode] = useState(null)
  const [showInAppModal, setShowInAppModal] = useState(false)

  const { isTelegram, openInExternalBrowser } = useIsTelegram()

  useEffect(() => {
    if (authCode) {
      googleIdToken({ code: authCode.code })
        .then((res) => {
          callback({
            providerUserToken: res.data.payload,
          })
        })
        .catch((err) => console.log(err))
    }
  }, [authCode])

  const login = useGoogleLogin({
    onSuccess: setAuthCode,
    onError: (err: any) => {
      setDisabled(err.error !== 'popup_closed_by_user')
    },
    flow: 'auth-code',
  })

  const handleClick = () => {
    if (isTelegram) {
      openInExternalBrowser()
      setShowInAppModal(true)
      return
    }
    login()
  }

  return (
    <>
      <GoogleButton disabled={disabled} onClick={handleClick} {...props} />
      <InAppBrowserModal visible={showInAppModal} onClose={() => setShowInAppModal(false)} />
    </>
  )
}

const GoogleAuth: FC<IGoogleAuth> = ({ callback, ...props }) => {
  const [clientId, setClientId] = useState()

  const [, setRetryCount] = useState(3)

  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const getAppPreferences = async () => {
      try {
        setIsLoading(true)

        const res = await getPreferences()
        const { googleAuthClientId } = res.data.payload

        setClientId(googleAuthClientId)

        setIsLoading(false)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)

        setRetryCount((prevState) => {
          if (prevState === 0) {
            setIsLoading(false)

            return 0
          }

          setTimeout(() => getAppPreferences(), 3000)

          return prevState - 1
        })
      }
    }

    getAppPreferences()
  }, [])

  return clientId ? (
    <GoogleOAuthProvider clientId={clientId}>
      <GoogleButtonContainer callback={callback} {...props} />
    </GoogleOAuthProvider>
  ) : (
    <GoogleButton disabled loading={isLoading} {...props} />
  )
}

export default GoogleAuth
